import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { Dispatch, useEffect, useState } from "react";
import convertToFilterState from "../helpers/convertToFilterState";
import convertToQueryParams from "../helpers/convertToQueryParams";
import {
  FilterAction,
  FilterActions,
  VehicleFilter,
} from "../types/vehicleFilter";
import useGetVehicleFilterData from "./useVehicleFilterData";

function convertToRecord(query: ParsedUrlQuery) {
  const queryRecord: Record<string, string> = {};
  Object.keys(query).forEach((q) => {
    queryRecord[q] = query[q]?.toString() ?? "";
  });
  return queryRecord;
}

function isUpToDate(
  query: Record<string, string>,
  state: Record<string, string>
): boolean {
  const qSorted = Object.keys(query)
    .sort()
    .reduce((obj: Record<string, string>, key) => {
      const newObj = { ...obj };
      newObj[key] = query[key];
      return newObj;
    }, {});
  const sSorted = Object.keys(state)
    .sort()
    .reduce((obj: Record<string, string>, key) => {
      const newObj = { ...obj };
      newObj[key] = state[key];
      return newObj;
    }, {});
  return JSON.stringify(qSorted) === JSON.stringify(sSorted);
}

export default function useFiltersQuerystringSync(
  vehicleFilters: VehicleFilter[],
  dispatch: Dispatch<FilterAction>
) {
  const { isReady, query, replace, pathname, locale } = useRouter();
  const [loadedFilters, setLoadedFilters] = useState<boolean>(false);
  const [isSynced, setIsSynced] = useState<boolean>(false);
  const { vehicleFilters: filterData } = useGetVehicleFilterData();

  useEffect(() => {
    if (!isReady) return;
    if (loadedFilters) return;
    if (filterData == null) return;

    // Load initial filters from query params
    dispatch({
      type: FilterActions.SetInitialFilters,
      payload: convertToFilterState(query, filterData),
    });

    setLoadedFilters(true);
  }, [dispatch, filterData, isReady, loadedFilters, query]);

  useEffect(() => {
    if (!isReady) return;
    if (!loadedFilters) return;

    // Check if params have changed
    const vehicleFiltersParams = convertToQueryParams(vehicleFilters, locale);
    const upToDate = isUpToDate(convertToRecord(query), vehicleFiltersParams);
    setIsSynced(upToDate);

    if (upToDate) return;

    // Sync filters to query params
    replace(
      {
        pathname,
        query: vehicleFiltersParams,
      },
      undefined,
      {
        shallow: true,
      }
    );
  }, [
    isReady,
    loadedFilters,
    locale,
    pathname,
    query,
    replace,
    vehicleFilters,
  ]);

  return {
    isSynced,
  };
}
